import { useMutation, useQuery, useLazyQuery } from '@apollo/client';
import { useCallback } from 'react';
import {
  GENERATE_FICTITIOUS_INVOICES,
  GET_AVAILABLE_PRODUCTS,
  GET_PRODUCT_SUMMARY,
  CALCULATE_LINE_OPTIONS,
  CHECK_MISSING_ELECTRONIC_INVOICES,
  SYNC_MISSING_ELECTRONIC_INVOICES,
  GenerateFictitiousInvoicesInput,
  InvoiceGenerationResult,
  AvailableProduct,
  ProductSummary,
  LineOptionsResult,
  MissingElectronicInvoicesResult,
  SyncElectronicInvoicesResult,
} from '../graphql/invoiceGenerationOperations';

/**
 * Hook for generating fictitious invoices
 */
export const useGenerateFictitiousInvoices = () => {
  const [generateInvoices, { loading, error }] = useMutation<{
    generateFictitiousInvoices: InvoiceGenerationResult;
  }>(GENERATE_FICTITIOUS_INVOICES, {
    // Refetch relevant queries after mutation
    refetchQueries: [
      'GetDashboardStats',
      'GetInvoices',
      'GetSalesTargetStatus',
      'GetTaxAmountStatus',
    ],
  });

  const execute = useCallback(
    async (input: GenerateFictitiousInvoicesInput) => {
      const result = await generateInvoices({
        variables: {
          input: {
            prefix: input.prefix,
            invoiceCount: input.invoiceCount,
            startDate: input.startDate.toISOString(),
            endDate: input.endDate.toISOString(),
            linesPerInvoice: input.linesPerInvoice,
            includeTip: input.includeTip,
            numberingMode: input.numberingMode,
            manualStartNumber: input.manualStartNumber,
            customTotalAmount: input.customTotalAmount,
          },
        },
      });
      return result.data?.generateFictitiousInvoices;
    },
    [generateInvoices]
  );

  return {
    generateFictitiousInvoices: execute,
    loading,
    error,
  };
};

/**
 * Hook for fetching available products
 */
export const useAvailableProducts = () => {
  const { data, loading, error, refetch } = useQuery<{
    getAvailableProducts: AvailableProduct[];
  }>(GET_AVAILABLE_PRODUCTS, {
    fetchPolicy: 'cache-and-network',
  });

  return {
    products: data?.getAvailableProducts || [],
    loading,
    error,
    refetch,
  };
};

/**
 * Hook for fetching product summary
 */
export const useProductSummary = () => {
  const { data, loading, error, refetch } = useQuery<{
    getProductSummary: ProductSummary;
  }>(GET_PRODUCT_SUMMARY, {
    fetchPolicy: 'cache-and-network',
  });

  return {
    summary: data?.getProductSummary,
    loading,
    error,
    refetch,
  };
};

/**
 * Hook for calculating line options (lazy query)
 */
export const useCalculateLineOptions = () => {
  const [calculate, { data, loading, error }] = useLazyQuery<{
    calculateLineOptions: LineOptionsResult;
  }>(CALCULATE_LINE_OPTIONS, {
    fetchPolicy: 'network-only',
  });

  const calculateOptions = useCallback(
    async (invoiceCount: number) => {
      const result = await calculate({
        variables: { invoiceCount },
      });
      return result.data?.calculateLineOptions;
    },
    [calculate]
  );

  return {
    calculateOptions,
    options: data?.calculateLineOptions,
    loading,
    error,
  };
};

/**
 * Hook for checking missing electronic invoices (lazy query)
 */
export const useCheckMissingElectronicInvoices = () => {
  const [check, { data, loading, error }] = useLazyQuery<{
    checkMissingElectronicInvoices: MissingElectronicInvoicesResult;
  }>(CHECK_MISSING_ELECTRONIC_INVOICES, {
    fetchPolicy: 'network-only',
  });

  const checkMissing = useCallback(
    async (prefix?: string, startDate?: Date, endDate?: Date) => {
      const result = await check({
        variables: {
          prefix,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
        },
      });
      return result.data?.checkMissingElectronicInvoices;
    },
    [check]
  );

  return {
    checkMissing,
    result: data?.checkMissingElectronicInvoices,
    loading,
    error,
  };
};

/**
 * Hook for syncing missing electronic invoices
 */
export const useSyncMissingElectronicInvoices = () => {
  const [syncMutation, { loading, error }] = useMutation<{
    syncMissingElectronicInvoices: SyncElectronicInvoicesResult;
  }>(SYNC_MISSING_ELECTRONIC_INVOICES);

  const syncMissing = useCallback(
    async (prefix?: string, limit?: number, startDate?: Date, endDate?: Date) => {
      const result = await syncMutation({
        variables: {
          prefix,
          limit,
          startDate: startDate?.toISOString(),
          endDate: endDate?.toISOString(),
        },
      });
      return result.data?.syncMissingElectronicInvoices;
    },
    [syncMutation]
  );

  return {
    syncMissing,
    loading,
    error,
  };
};
